import { motion } from "framer-motion";
import { fadeIn } from "Features/LandingPage/Common";
import { useAnimateOnScroll } from "Hooks/useAnimateOnScroll";
import {
  TrendingUp,
  Shield,
  Clock,
  DollarSign,
  Star,
  CheckCircle,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { StyledButton as Button } from "Components/StyledButton";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";

export const Finance = () => {
  const [financeRef, financeControls] = useAnimateOnScroll(0.1);
  const navigate = useNavigate();
  return (
    <motion.section
      // @ts-ignore
      ref={financeRef}
      initial="hidden"
      // @ts-ignore
      animate={financeControls}
      variants={fadeIn}
      className="py-16 md:py-20 relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-green-100 w-full"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Partner Logos */}
        <section className="mb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  See Personalized Loan Offers
                </h2>
                <p className="text-xl text-gray-600">
                  Get the best deal — without damaging your credit.
                </p>
                <div className="space-y-4 text-base flex justify-center items-center flex-col md:flex-row  gap-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">
                      Rates as low as 4.5% APR
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">
                      Soft credit check only
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">
                      Get offers in 60 seconds
                    </span>
                  </div>
                </div>
                <Button
                  className="bg-[#80E280] hover:bg-[#70D270] text-gray-900 font-semibold px-8 py-4 text-lg w-full md:w-[90%]"
                  onClick={() => {
                    navigate(
                      ROUTES.COLLEGE_FINDER_NEXT.replace(
                        ":next",
                        ROUTES.FINANCE
                      )
                    );
                  }}
                >
                  Check Your Rate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <div className="text-base font-bold text-gray-500 text-center">
                  Interest rates as low as{" "}
                  <span className="text-red-600">3.04%*</span>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 shadow-xl">
                  {/* Animated Student Getting Loan Illustration */}
                  <div className="space-y-6">
                    {/* Student Avatar */}
                    <div className="flex justify-center">
                      <div className="relative">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center animate-pulse">
                          <GraduationCap className="w-10 h-10 text-white" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full animate-bounce">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>

                    {/* Loan Process Steps Animation */}
                    <div className="space-y-4">
                      {/* Step 1: Application */}
                      <div
                        className="flex items-center gap-4 animate-fade-in-up"
                        style={{ animationDelay: "0.5s" }}
                      >
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          1
                        </div>
                        <div className="flex-1 bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-sm font-medium text-gray-900">
                            Submit Application
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-green-500 h-2 rounded-full animate-pulse"
                              style={{ width: "100%" }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Step 2: Review */}
                      <div
                        className="flex items-center gap-4 animate-fade-in-up"
                        style={{ animationDelay: "1s" }}
                      >
                        <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          2
                        </div>
                        <div className="flex-1 bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-sm font-medium text-gray-900">
                            Lender Review
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-yellow-500 h-2 rounded-full animate-pulse"
                              style={{ width: "75%" }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Step 3: Approval */}
                      <div
                        className="flex items-center gap-4 animate-fade-in-up"
                        style={{ animationDelay: "1.5s" }}
                      >
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          3
                        </div>
                        <div className="flex-1 bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-sm font-medium text-gray-900">
                            Loan Approved!
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-green-500 h-2 rounded-full animate-pulse"
                              style={{ width: "100%" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Money Animation */}
                    <div className="flex justify-center">
                      <div className="relative">
                        <div className="flex gap-2">
                          {[1, 2, 3].map((i) => (
                            <div
                              key={i}
                              className="w-12 h-8 bg-green-500 rounded flex items-center justify-center animate-bounce"
                              style={{ animationDelay: `${i * 0.2}s` }}
                            >
                              <DollarSign className="w-6 h-6 text-white" />
                            </div>
                          ))}
                        </div>
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium animate-pulse">
                            Funds Disbursed!
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Success Message */}
                    <div
                      className="text-center animate-fade-in"
                      style={{ animationDelay: "2s" }}
                    >
                      <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                        <Star className="w-4 h-4" />
                        Dream University Here I Come!
                        <Star className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-500 rounded-full animate-ping opacity-75"></div>
                <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>
        <motion.div variants={fadeIn} className="text-center">
          <p className="text-gray-500 text-base mb-8">
            Trusted by leading financial institutions
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60">
            <div className="text-center">
              <div className="font-bold text-lg text-gray-700">ICICI Bank</div>
              <div className="text-sm text-gray-500">Trusted Partner</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg text-gray-700">MPOWER</div>
              <div className="text-sm text-gray-500">Global Partner</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg text-gray-700">
                Prodigy Finance
              </div>
              <div className="text-sm text-gray-500">Backed by</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg text-gray-700">Credila</div>
              <div className="text-sm text-gray-500">Supported by</div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};
